import * as React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  OutlinedInput,
  InputAdornment,
  Button,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { providerChangePassword } from "../../../api/provider-section/provider-profile.api";
import "./change-password-modal.scss";

type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // optional callback after success
};

const passwordSchema = Yup.string()
  .required("Password is required")
  .min(8, "Password must be at least 8 characters")
  .matches(/[A-Z]/, "Must include at least 1 uppercase letter")
  .matches(/[a-z]/, "Must include at least 1 lowercase letter")
  .matches(/[0-9]/, "Must include at least 1 number")
  .matches(/[^A-Za-z0-9]/, "Must include at least 1 special character");

const validationSchema = Yup.object({
  currentPassword: Yup.string()
    .required("Current password is required"),
  newPassword: passwordSchema.notOneOf(
    [Yup.ref("currentPassword")],
    "New password must be different from current password"
  ),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
});

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [submitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string>("");

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setApiError("");
      setSubmitting(true);

      try {
        // x-www-form-urlencoded (as per curl)
        const body = new URLSearchParams();
        body.append("currentPassword", values.currentPassword);
        body.append("newPassword", values.newPassword);
        body.append("confirmPassword", values.confirmPassword);

        const res = await providerChangePassword(body);

        if (res?.data?.success) {
          formik.resetForm();
          onClose();
          onSuccess?.();
          return;
        }

        // fallback if API returns different structure
        setApiError(res?.data?.message || "Failed to change password.");
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          "Failed to change password.";
        setApiError(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const touched = formik.touched;
  const errors = formik.errors;

  const closeAndReset = () => {
    setApiError("");
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      classes={{ paper: "changePasswordModal__paper" }}
    >
      <Box className="changePasswordModal__header">
        <Typography className="changePasswordModal__title">
          Change Password
        </Typography>

        <IconButton
          className="changePasswordModal__close"
          onClick={closeAndReset}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="changePasswordModal__divider" />

      <DialogContent className="changePasswordModal__content">
        {apiError ? (
          <Box className="changePasswordModal__apiError">{apiError}</Box>
        ) : null}

        {/* Current Password */}
        <Box className="changePasswordModal__fieldBlock">
          <Typography className="changePasswordModal__label">
            Current Password <span className="changePasswordModal__req">*</span>
          </Typography>

          <OutlinedInput
            className={`changePasswordModal__input ${
              touched.currentPassword && errors.currentPassword ? "hasError" : ""
            }`}
            fullWidth
            placeholder="Enter current password"
            type={showCurrent ? "text" : "password"}
            name="currentPassword"
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowCurrent((p) => !p)}
                  tabIndex={-1}
                >
                  {showCurrent ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />

          {touched.currentPassword && errors.currentPassword ? (
            <FormHelperText className="changePasswordModal__helper error">
              {errors.currentPassword}
            </FormHelperText>
          ) : null}
        </Box>

        {/* New Password */}
        <Box className="changePasswordModal__fieldBlock">
          <Typography className="changePasswordModal__label">
            New Password <span className="changePasswordModal__req">*</span>
          </Typography>

          <OutlinedInput
            className={`changePasswordModal__input ${
              touched.newPassword && errors.newPassword ? "hasError" : ""
            }`}
            fullWidth
            placeholder="Enter new password (min. 8 characters)"
            type={showNew ? "text" : "password"}
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowNew((p) => !p)}
                  tabIndex={-1}
                >
                  {showNew ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />

          {touched.newPassword && errors.newPassword ? (
            <FormHelperText className="changePasswordModal__helper error">
              {errors.newPassword}
            </FormHelperText>
          ) : null}
        </Box>

        {/* Confirm Password */}
        <Box className="changePasswordModal__fieldBlock">
          <Typography className="changePasswordModal__label">
            Confirm New Password{" "}
            <span className="changePasswordModal__req">*</span>
          </Typography>

          <OutlinedInput
            className={`changePasswordModal__input ${
              touched.confirmPassword && errors.confirmPassword ? "hasError" : ""
            }`}
            fullWidth
            placeholder="Re-enter new password"
            type={showConfirm ? "text" : "password"}
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowConfirm((p) => !p)}
                  tabIndex={-1}
                >
                  {showConfirm ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />

          {touched.confirmPassword && errors.confirmPassword ? (
            <FormHelperText className="changePasswordModal__helper error">
              {errors.confirmPassword}
            </FormHelperText>
          ) : null}
        </Box>

        {/* Buttons */}
        <Box className="changePasswordModal__actions">
          <Button
            variant="outlined"
            className="changePasswordModal__btn cancel"
            onClick={closeAndReset}
            disabled={submitting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            className="changePasswordModal__btn primary"
            onClick={() => {
              formik.setTouched(
                {
                  currentPassword: true,
                  newPassword: true,
                  confirmPassword: true,
                },
                true
              );
              formik.handleSubmit();
            }}
            disabled={submitting}
          >
            Change Password
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
